/**
 * Hook para el Claim Assistant Agent — sesiones + streaming SSE + aprobar.
 *
 * Endpoint: /api/claim-assistant/* (cookie auth, mismo origen).
 *
 * Uso típico:
 *   const ca = useClaimAssistant({ mlClaimId, sellerId });
 *   ca.ensureSession() // crea o recupera
 *   ca.streamTurn("Analizá este claim", onChunk)
 *   ca.approve(draft, 'complainant')
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_BASE = '/api/claim-assistant';

export interface CaSession {
  id: number;
  ml_claim_id: number;
  seller_id: number;
  sdk_session_id: string;
  status: 'active' | 'closed' | 'error';
  model: string;
  total_tokens_in: number;
  total_tokens_out: number;
  total_cost_usd: number;
  created_at: string;
  closed_at: string | null;
}

export interface CaMessage {
  id: number;
  session_id: number;
  role: 'user' | 'assistant' | 'tool' | 'system';
  content: Record<string, unknown>;
  tokens_in: number | null;
  tokens_out: number | null;
  created_at: string;
}

export interface CaSentRow {
  id: number;
  session_id: number;
  draft_text: string;
  char_count: number;
  receiver_role: string;
  approved_by: string | null;
  ml_response: Record<string, unknown> | null;
  sent_at: string;
}

export interface DraftCandidate {
  text: string;
  tone: string;
  strategy: string;
  receiver_role: 'complainant' | 'mediator' | 'respondent';
  char_count: number;
  batch_id?: string | null;
  superseded?: boolean;
  message_id?: number;
}

/** Datos pre-llenados de la evidencia de envío (upload_shipping_evidence) —
 *  el backend los arma desde el shipment; la card los muestra porque la
 *  evidencia es inmutable una vez enviada (el operador revisa ANTES). */
export interface ShippingEvidencePrefill {
  shipping_method: string;
  shipping_company_name: string;
  date_shipped: string;
  tracking_number?: string;
}

export interface ActionCandidate {
  action_type: 'allow_return' | 'open_dispute' | 'upload_shipping_evidence';
  justificacion: string;
  resultado_esperado: string;
  envio?: ShippingEvidencePrefill | null;
  message_id?: number;
}

/** Respuesta de execute-action. manual_required=true cuando la API de ML
 *  rechazó la acción (CBT) y hay que hacerla a mano desde la web de ML. */
export interface ExecuteActionResult {
  success: boolean;
  manual_required?: boolean;
  web_url?: string | null;
  error?: string;
  data?: {
    ml_response: Record<string, unknown> | null;
    verification: {
      antes: { stage: string | null; status: string | null };
      despues: { stage: string | null; status: string | null };
      acciones_despues: unknown;
    };
  };
}

/** Parser SSE simple: yield-ea {event, data} mientras llegan chunks. */
async function* parseSseStream(body: ReadableStream<Uint8Array>) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buf.indexOf('\n\n')) !== -1) {
        const block = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        let event = 'message';
        const dataLines: string[] = [];
        for (const line of block.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
        }
        const raw = dataLines.join('\n');
        let data: unknown = raw;
        try {
          data = JSON.parse(raw);
        } catch {
          /* keep as string */
        }
        yield { event, data };
      }
    }
  } finally {
    reader.releaseLock();
  }
}

export type StreamChunk =
  | { kind: 'text'; text: string }
  | { kind: 'tool_use'; name: string; input: Record<string, unknown> }
  | { kind: 'tool_result'; ok: boolean; preview: string }
  | { kind: 'draft'; draft: DraftCandidate }
  | { kind: 'final'; cost_usd: number; tokens_in: number; tokens_out: number }
  | { kind: 'error'; message: string };

interface UseClaimAssistantOpts {
  mlClaimId: number;
  sellerId: number;
  /** Base alternativa (mini-app firmada: '/api/claim-app/assistant'). Default: '/api/claim-assistant'. */
  baseUrl?: string;
  /** Token firmado del enlace. Si se pasa, auth por Bearer + ?t= en vez de cookie. */
  token?: string;
}

interface MlResponseShape {
  [k: string]: unknown;
}

export function useClaimAssistant({ mlClaimId, sellerId, baseUrl, token }: UseClaimAssistantOpts) {
  const [session, setSession] = useState<CaSession | null>(null);
  const [messages, setMessages] = useState<CaMessage[]>([]);
  const [sent, setSent] = useState<CaSentRow[]>([]);
  const [drafts, setDrafts] = useState<DraftCandidate[]>([]);
  const [supersededDrafts, setSupersededDrafts] = useState<DraftCandidate[]>([]);
  const [actionCandidates, setActionCandidates] = useState<ActionCandidate[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const base = baseUrl ?? DEFAULT_BASE;
  // Con token (mini-app firmada): seller va en el token → query lleva ?t=. Sin
  // token (pantalla con login): query lleva ?seller_id= y auth por cookie.
  const q = token ? `?t=${encodeURIComponent(token)}` : `?seller_id=${sellerId}`;
  const authHeader: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

  /** GET /sessions?ml_claim_id=X — devuelve sesión activa o null */
  const findActive = useCallback(async (): Promise<CaSession | null> => {
    const r = await fetch(`${base}/sessions${q}&ml_claim_id=${mlClaimId}`, {
      credentials: 'include',
      headers: authHeader,
    });
    const j = await r.json();
    return (j?.data as CaSession | null) ?? null;
  }, [base, mlClaimId, q]);

  /** POST /sessions — crea (idempotente por (claim, seller, active)) */
  const ensureSession = useCallback(async (): Promise<CaSession> => {
    const existing = await findActive();
    if (existing) {
      setSession(existing);
      await refresh(existing.id);
      return existing;
    }
    const r = await fetch(`${base}/sessions${q}`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...authHeader, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ml_claim_id: mlClaimId }),
    });
    const j = await r.json();
    if (!j.success) throw new Error(j.error || 'createSession failed');
    setSession(j.data);
    return j.data;
  }, [findActive, mlClaimId, q]);

  /** GET /sessions/:id — refresca historial + sent */
  const refresh = useCallback(
    async (sid: number) => {
      const r = await fetch(`${base}/sessions/${sid}${q}`, {
        credentials: 'include',
        headers: authHeader,
      });
      const j = await r.json();
      if (j.success) {
        setMessages(j.data.messages ?? []);
        setSent(j.data.sent ?? []);
        // extraer drafts persistidos del backend (cuando propose_draft corrió)
        const serverDrafts = (j.data.drafts as DraftCandidate[] | undefined) ?? null;
        let vigentes: DraftCandidate[];
        let reemplazados: DraftCandidate[];
        if (serverDrafts) {
          vigentes = serverDrafts.filter((d) => !d.superseded);
          reemplazados = serverDrafts.filter((d) => d.superseded);
        } else {
          // Fallback (backend viejo): comportamiento previo — todos vigentes.
          vigentes = [];
          for (const m of (j.data.messages as CaMessage[]) ?? []) {
            if (m.role === 'assistant' && (m.content as Record<string, unknown>)?.type === 'draft_candidate') {
              vigentes.push(m.content as unknown as DraftCandidate);
            }
          }
          reemplazados = [];
        }
        // Si traemos drafts y había un error previo (SSE cortado, network blip),
        // limpiamos: lo importante (los borradores) están — el error visual sólo
        // confunde al operador.
        if (vigentes.length > 0) setError(null);
        setDrafts(vigentes);
        setSupersededDrafts(reemplazados);
        // Acciones propuestas (propose_action): solo las del último turno con
        // propuestas — criterio simple: las que aparecen DESPUÉS del último
        // mensaje del usuario (mismo espíritu que el batch vigente de drafts).
        const msgs = (j.data.messages as CaMessage[]) ?? [];
        let lastUserIdx = -1;
        msgs.forEach((m, i) => {
          if (m.role === 'user') lastUserIdx = i;
        });
        const acts: ActionCandidate[] = [];
        for (const m of msgs.slice(lastUserIdx + 1)) {
          const c = m.content as Record<string, unknown>;
          if (m.role === 'assistant' && c?.type === 'action_candidate') {
            acts.push({
              action_type: c.action_type as ActionCandidate['action_type'],
              justificacion: String(c.justificacion ?? ''),
              resultado_esperado: String(c.resultado_esperado ?? ''),
              envio: (c.envio as ShippingEvidencePrefill | undefined) ?? null,
              message_id: m.id,
            });
          }
        }
        setActionCandidates(acts);
      }
    },
    [q]
  );

  /** POST /sessions/:id/messages — stream un turn del agente.
   *  Defensivo: si la sesión local está stale, re-resuelve activa antes de stream. */
  const streamTurn = useCallback(
    async (userMessage: string, onChunk?: (c: StreamChunk) => void): Promise<void> => {
      if (streaming) throw new Error('ya hay un turn en curso');

      let activeSid = session?.id;
      if (!activeSid) {
        const fresh = await findActive();
        if (!fresh) throw new Error('session not ready — call ensureSession first');
        setSession(fresh);
        activeSid = fresh.id;
      }

      setStreaming(true);
      setError(null);

      const ctl = new AbortController();
      abortRef.current = ctl;

      try {
        let res = await fetch(`${base}/sessions/${activeSid}/messages${q}`, {
          method: 'POST',
          credentials: 'include',
          headers: { ...authHeader, 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: userMessage }),
          signal: ctl.signal,
        });
        if (res.status === 404 || res.status === 409) {
          // Sesión stale o cerrada — re-resolver y reintentar.
          const fresh = await findActive();
          if (fresh) {
            setSession(fresh);
            activeSid = fresh.id;
            res = await fetch(`${base}/sessions/${activeSid}/messages${q}`, {
              method: 'POST',
              credentials: 'include',
              headers: { ...authHeader, 'Content-Type': 'application/json' },
              body: JSON.stringify({ message: userMessage }),
              signal: ctl.signal,
            });
          }
        }
        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

        for await (const { event, data } of parseSseStream(res.body)) {
          if (event === 'done') break;
          if (event === 'error') {
            const msg = (data as { error?: string })?.error ?? 'error desconocido';
            setError(msg);
            onChunk?.({ kind: 'error', message: msg });
            continue;
          }
          const wrapper = data as { type?: string; message?: Record<string, unknown> };
          const inner = wrapper?.message as Record<string, unknown> | undefined;
          if (wrapper?.type === 'final' && inner?.type === 'result' && inner?.subtype === 'success') {
            const usage = (inner as { usage?: { input_tokens?: number; output_tokens?: number } })
              .usage;
            onChunk?.({
              kind: 'final',
              cost_usd: ((inner as { total_cost_usd?: number }).total_cost_usd ?? 0) as number,
              tokens_in: usage?.input_tokens ?? 0,
              tokens_out: usage?.output_tokens ?? 0,
            });
            continue;
          }
          if (inner?.type === 'assistant') {
            const am = (inner as { message?: { content?: Array<Record<string, unknown>> } }).message;
            for (const block of am?.content ?? []) {
              if (block.type === 'text' && typeof block.text === 'string') {
                // Defensa: prompts viejos podían emitir <drafts>{JSON}</drafts>
                // como texto crudo. Los filtramos — las cards salen vía
                // propose_draft que se persiste como content.type=draft_candidate.
                const cleaned = stripDraftsBlocks(block.text);
                if (cleaned.trim()) onChunk?.({ kind: 'text', text: cleaned });
              } else if (block.type === 'tool_use') {
                onChunk?.({
                  kind: 'tool_use',
                  name: String(block.name ?? ''),
                  input: (block.input as Record<string, unknown>) ?? {},
                });
              }
            }
          } else if (inner?.type === 'user') {
            // tool result block llega como user content
            const um = (inner as { message?: { content?: Array<Record<string, unknown>> } }).message;
            for (const block of um?.content ?? []) {
              if (block.type === 'tool_result') {
                const rc = block.content as Array<{ type: string; text?: string }> | undefined;
                const preview = (rc?.[0]?.text ?? '').slice(0, 200);
                onChunk?.({
                  kind: 'tool_result',
                  ok: !((block as { is_error?: boolean }).is_error ?? false),
                  preview,
                });
              }
            }
          }
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        onChunk?.({ kind: 'error', message: msg });
        // Recovery automático: si el SSE se cortó (nginx timeout, browser drop,
        // network blip), el backend probablemente siguió ejecutando y persistió
        // drafts en DB. El finally ya hace UN refresh inmediato; éste segundo
        // refresh a los 4s trae lo que llegó después del corte.
        if (activeSid) {
          const sidCopy = activeSid;
          setTimeout(() => {
            refresh(sidCopy).catch(() => {
              /* swallow — UX no se beneficia de un toast extra */
            });
          }, 4000);
        }
      } finally {
        setStreaming(false);
        abortRef.current = null;
        if (activeSid) await refresh(activeSid);
      }
    },
    [session, streaming, q, refresh, findActive]
  );

  /** POST /sessions/:id/approve — envía draft a ML y persiste sent.
   *  Defensivo contra state stale: si el ID local apunta a una sesión que ya
   *  no está activa (DELETE manual, otra pestaña que cerró), re-resuelve la
   *  sesión activa real y reintenta UNA vez. */
  const approve = useCallback(
    async (
      draft: { text: string; receiver_role: 'complainant' | 'mediator' },
      approvedBy?: string
    ) => {
      const body = JSON.stringify({
        draft_text: draft.text,
        receiver_role: draft.receiver_role,
        approved_by: approvedBy ?? null,
      });

      async function tryApprove(sid: number) {
        return fetch(`${base}/sessions/${sid}/approve${q}`, {
          method: 'POST',
          credentials: 'include',
          headers: { ...authHeader, 'Content-Type': 'application/json' },
          body,
        });
      }

      let sid = session?.id;
      if (!sid) {
        const found = await findActive();
        if (!found) throw new Error('session not ready');
        setSession(found);
        sid = found.id;
      }

      let r = await tryApprove(sid);
      if (r.status === 404 || r.status === 403) {
        // State stale: la sesión que teníamos ya no existe / no es nuestra.
        const fresh = await findActive();
        if (!fresh) throw new Error('session no encontrada y no hay activa');
        setSession(fresh);
        sid = fresh.id;
        r = await tryApprove(sid);
      }
      const j = (await r.json()) as {
        success: boolean;
        data?: { ml_response?: MlResponseShape };
        error?: string;
      };
      if (!j.success) throw new Error(j.error || 'approve failed');
      await refresh(sid);
      return j.data;
    },
    [session, q, refresh, findActive]
  );

  /** POST /sessions/:id/execute-action — ejecuta una acción propuesta en ML.
   *  Devuelve el resultado completo: si manual_required=true NO tira error —
   *  el caller muestra el link "Abrir en Mercado Libre" (web_url). */
  const executeAction = useCallback(
    async (action_type: ActionCandidate['action_type']): Promise<ExecuteActionResult> => {
      let sid = session?.id;
      if (!sid) {
        const found = await findActive();
        if (!found) throw new Error('session not ready');
        setSession(found);
        sid = found.id;
      }
      const r = await fetch(`${base}/sessions/${sid}/execute-action${q}`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action_type, approved_by: 'ui-operator' }),
      });
      const j = (await r.json()) as ExecuteActionResult;
      if (!j.success && !j.manual_required) {
        throw new Error(j.error || 'execute-action failed');
      }
      await refresh(sid);
      return j;
    },
    [session, q, refresh, findActive]
  );

  /** POST /sessions/:id/reset — "Empezar de cero": borra mensajes+borradores
   *  en la DB y deja al motor sin memoria de la charla. Los mensajes ya
   *  ENVIADOS a ML no se borran (registro legal). 404 = backend viejo aún sin
   *  reiniciar → mensaje claro en vez de error críptico. */
  const resetSession = useCallback(async () => {
    if (!session) throw new Error('session not ready');
    const r = await fetch(`${base}/sessions/${session.id}/reset${q}`, {
      method: 'POST',
      credentials: 'include',
      headers: authHeader,
    });
    if (r.status === 404) {
      throw new Error('Esta función se activa con el próximo reinicio del sistema.');
    }
    const j = await r.json();
    if (!j.success) throw new Error(j.error || 'reset failed');
    setMessages([]);
    setDrafts([]);
    setSupersededDrafts([]);
    setError(null);
    await refresh(session.id);
  }, [session, q, refresh]);

  const closeSession = useCallback(async () => {
    if (!session) return;
    await fetch(`${base}/sessions/${session.id}${q}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: authHeader,
    });
    setSession(null);
  }, [session, q]);

  const cancelStream = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  // Cleanup en unmount: cancela stream, NO cierra sesión (puede haber drafts útiles).
  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  return {
    session,
    messages,
    sent,
    drafts,
    supersededDrafts,
    actionCandidates,
    streaming,
    error,
    ensureSession,
    refresh,
    streamTurn,
    approve,
    executeAction,
    resetSession,
    closeSession,
    cancelStream,
  };
}

/**
 * Filtra bloques `<drafts>...</drafts>` del texto streamed por el agente.
 * Defensa para prompts viejos que emitían JSON inline en vez de usar la tool
 * propose_draft. Los drafts reales aparecen como cards (content.type=draft_candidate
 * persistido en DB) — no como texto.
 */
function stripDraftsBlocks(text: string): string {
  return text.replace(/<drafts>[\s\S]*?<\/drafts>/g, '').replace(/<drafts>[\s\S]*$/g, '');
}
